import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { fetchHelpCenterBrandingAction } from 'epics/model/help-center';
import { fetchOrganisationsModelAction } from 'epics/model/organisations';
import { fetchUserRequestsCountsAction } from 'epics/model/user-requests-counts';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Conversation } from 'unified-help-center-alpha/src/components/conversation/Conversation';
import Blanket from '@atlaskit/blanket';
import FadeIn from '@atlaskit/motion/fade-in';
import Portal from '@atlaskit/portal';
import { Box, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { ScrollLock } from '@atlassian/help-center-common-component/scroll-lock';
import { ConversationNavBar } from '../conversation-nav-bar';
import type { conversationModalQuery } from './__generated__/conversationModalQuery.graphql';

interface ConversationModalProps {
    fetchUserRequestsCounts: typeof fetchUserRequestsCountsAction;
    fetchOrganisations: typeof fetchOrganisationsModelAction;
    fetchBranding: typeof fetchHelpCenterBrandingAction;
}

const ConversationModal = ({ fetchOrganisations, fetchUserRequestsCounts, fetchBranding }: ConversationModalProps) => {
    const data = useLazyLoadQuery<conversationModalQuery>(
        graphql`
            query conversationModalQuery {
                me @required(action: THROW) {
                    user @required(action: THROW) {
                        ...ConversationUser
                    }
                }
            }
        `,
        {}
    );

    useEffect(() => {
        fetchBranding();
        fetchOrganisations();
        fetchUserRequestsCounts();
    }, [fetchBranding, fetchOrganisations, fetchUserRequestsCounts]);

    return (
        <FadeIn duration={200} entranceDirection="bottom">
            {() => (
                <Portal zIndex={layers.modal()}>
                    <Box xcss={conversationModalStyles}>
                        {/*Disabling existing violations, should be fixed when revisited.*/}
                        {/* Ensures scroll events are blocked on the document body and locked */}
                        <ScrollLock />
                        <Blanket shouldAllowClickThrough={false} isTinted>
                            <Box
                                // TODO: Get rid of this when removing nav bar according to new designs to be done as a follow up
                                xcss={xcss({
                                    height: `calc(100% - ${'56px'})`,
                                })}
                            >
                                <ConversationNavBar />
                                <Box xcss={conversationBoxStyles}>
                                    <Box
                                        xcss={xcss({
                                            width: '100%',
                                            pointerEvents: 'auto',
                                            display: 'flex',
                                            height: '100%',
                                        })}
                                    >
                                        <Conversation key="CONVERSATION" userRef={data.me.user} />
                                    </Box>
                                </Box>
                            </Box>
                        </Blanket>
                    </Box>
                </Portal>
            )}
        </FadeIn>
    );
};

export default connect(null, {
    fetchOrganisations: fetchOrganisationsModelAction,
    fetchUserRequestsCounts: fetchUserRequestsCountsAction,
    fetchBranding: fetchHelpCenterBrandingAction,
})(ConversationModal);

const conversationModalStyles = xcss({
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    top: '0px',
    left: '0px',
});

const conversationBoxStyles = xcss({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    height: '100%',
    top: '56px',
    backgroundColor: 'elevation.surface.overlay',
    paddingBottom: token('space.300', '24px'),
    overflow: 'auto',
});
